import { useMemo, useState } from "react";
import {
  FaArrowLeft,
  FaChevronDown,
  FaCloudUploadAlt,
  FaEllipsisH,
  FaFileExcel,
  FaFilePdf,
  FaFileWord,
  FaFolder,
  FaFolderOpen,
  FaLock,
  FaPlus,
  FaSearch,
  FaShareAlt,
  FaTimes,
  FaUsers,
} from "react-icons/fa";

type Visibility = "private" | "selected" | "everyone";
type Role = "owner" | "editor" | "viewer";

type Staff = {
  id: string;
  name: string;
  role: string;
  avatar?: string;
};

type DocumentFile = {
  id: string;
  name: string;
  type: "pdf" | "docs" | "excel" | "image";
  size: string;
  uploadedBy: string;
  updatedAt: string;
  access: "private" | "selected" | "everyone";
};

type Folder = {
  id: string;
  name: string;
  description: string;
  ownerId: string;
  visibility: Visibility;
  allowFileUploads: boolean;
  files: DocumentFile[];
  members: {
    staffId: string;
    role: Role;
  }[];
  updatedAt: string;
};

const staff: Staff[] = [
  {
    id: "1",
    name: "Wisdom Okposin",
    role: "Software Engineer",
  },
  {
    id: "2",
    name: "John Doe",
    role: "Project Manager",
  },
  {
    id: "3",
    name: "Mary James",
    role: "HR Manager",
  },
  {
    id: "4",
    name: "David Okon",
    role: "Site Engineer",
  },
  {
    id: "5",
    name: "Esther Williams",
    role: "Accountant",
  },
];

const initialFolders: Folder[] = [
  {
    id: "folder-1",
    name: "Project Documentation",
    description: "Documents and resources for ongoing projects.",
    ownerId: "1",
    visibility: "selected",
    allowFileUploads: true,
    updatedAt: "Today",
    members: [
      { staffId: "1", role: "owner" },
      { staffId: "2", role: "editor" },
      { staffId: "4", role: "viewer" },
    ],
    files: [
      {
        id: "file-1",
        name: "Project Proposal.docx",
        type: "docs",
        size: "2.4 MB",
        uploadedBy: "Wisdom Okposin",
        updatedAt: "Today",
        access: "selected",
      },
      {
        id: "file-2",
        name: "Project Contract.pdf",
        type: "pdf",
        size: "1.8 MB",
        uploadedBy: "Wisdom Okposin",
        updatedAt: "Yesterday",
        access: "selected",
      },
      {
        id: "file-3",
        name: "Project Budget.xlsx",
        type: "excel",
        size: "845 KB",
        uploadedBy: "John Doe",
        updatedAt: "Sep 24",
        access: "everyone",
      },
    ],
  },
  {
    id: "folder-2",
    name: "HR Documents",
    description: "Internal HR documents and employee resources.",
    ownerId: "3",
    visibility: "selected",
    allowFileUploads: false,
    updatedAt: "Yesterday",
    members: [
      { staffId: "3", role: "owner" },
      { staffId: "1", role: "viewer" },
      { staffId: "5", role: "editor" },
    ],
    files: [
      {
        id: "file-4",
        name: "Employee Handbook.pdf",
        type: "pdf",
        size: "4.2 MB",
        uploadedBy: "Mary James",
        updatedAt: "Yesterday",
        access: "selected",
      },
    ],
  },
  {
    id: "folder-3",
    name: "Site Reports",
    description: "Site inspection reports and project evidence.",
    ownerId: "4",
    visibility: "everyone",
    allowFileUploads: true,
    updatedAt: "Sep 22",
    members: [
      { staffId: "4", role: "owner" },
      { staffId: "1", role: "viewer" },
    ],
    files: [],
  },
];

export default function DocumentCenterPage() {
  const [folders, setFolders] = useState<Folder[]>(initialFolders);
  const [activeFolder, setActiveFolder] = useState<Folder | null>(null);
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState<
    "my-folders" | "shared" | "recent"
  >("my-folders");

  const [showCreateFolder, setShowCreateFolder] = useState(false);
  const [showManageAccess, setShowManageAccess] = useState(false);

  const [newFolderName, setNewFolderName] = useState("");
  const [newFolderDescription, setNewFolderDescription] = useState("");
  const [newFolderVisibility, setNewFolderVisibility] =
    useState<Visibility>("private");
  const [allowFileUploads, setAllowFileUploads] = useState(true);

  const currentUserId = "1";

  const myFolders = useMemo(() => {
    return folders.filter((folder) => folder.ownerId === currentUserId);
  }, [folders]);

  const sharedFolders = useMemo(() => {
    return folders.filter(
      (folder) =>
        folder.ownerId !== currentUserId &&
        folder.members.some((member) => member.staffId === currentUserId),
    );
  }, [folders]);

  const displayedFolders = useMemo(() => {
    let result = folders;

    if (activeTab === "my-folders") {
      result = myFolders;
    }

    if (activeTab === "shared") {
      result = sharedFolders;
    }

    if (search.trim()) {
      const query = search.toLowerCase();

      result = result.filter(
        (folder) =>
          folder.name.toLowerCase().includes(query) ||
          folder.description.toLowerCase().includes(query),
      );
    }

    return result;
  }, [folders, myFolders, sharedFolders, activeTab, search]);

  const getStaff = (id: string) =>
    staff.find((person) => person.id === id);

  const getFileIcon = (type: DocumentFile["type"]) => {
    if (type === "pdf") {
      return FaFilePdf({
        className: "text-red-500",
      });
    }

    if (type === "docs") {
      return FaFileWord({
        className: "text-blue-500",
      });
    }

    if (type === "excel") {
      return FaFileExcel({
        className: "text-emerald-500",
      });
    }

    return FaFolder({
      className: "text-indigo-500",
    });
  };

  const getVisibilityLabel = (visibility: Visibility) => {
    if (visibility === "private") return "Only me";
    if (visibility === "selected") return "Selected staff";
    return "Everyone";
  };

  const createFolder = () => {
    if (!newFolderName.trim()) return;

    const newFolder: Folder = {
      id: `folder-${Date.now()}`,
      name: newFolderName.trim(),
      description: newFolderDescription.trim(),
      ownerId: currentUserId,
      visibility: newFolderVisibility,
      allowFileUploads,
      updatedAt: "Just now",
      members: [
        {
          staffId: currentUserId,
          role: "owner",
        },
      ],
      files: [],
    };

    setFolders((prev) => [newFolder, ...prev]);

    setNewFolderName("");
    setNewFolderDescription("");
    setNewFolderVisibility("private");
    setAllowFileUploads(true);
    setShowCreateFolder(false);
  };

  if (activeFolder) {
    return (
      <FolderWorkspace
        folder={activeFolder}
        staff={staff}
        currentUserId={currentUserId}
        getFileIcon={getFileIcon}
        getStaff={getStaff}
        getVisibilityLabel={getVisibilityLabel}
        onBack={() => setActiveFolder(null)}
        onManageAccess={() => setShowManageAccess(true)}
        showManageAccess={showManageAccess}
        setShowManageAccess={setShowManageAccess}
      />
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-6">
      <div className="mx-auto w-full max-w-[1600px]">
        {/* Header */}
        <div className="mb-7 flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
                {FaFolderOpen({})}
              </div>

              <div>
                <h1 className="text-2xl font-bold text-slate-900">
                  Document Center
                </h1>

                <p className="mt-1 text-sm text-slate-500">
                  Organize, share and collaborate on workplace documents.
                </p>
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={() => setShowCreateFolder(true)}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-600 px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700"
          >
            {FaPlus({ className: "text-xs" })}
            New Folder
          </button>
        </div>

        {/* Search */}
        <div className="mb-6 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="relative">
            <div className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">
              {FaSearch({})}
            </div>

            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search folders and documents..."
              className="w-full rounded-xl border border-slate-200 bg-slate-50 py-3 pl-11 pr-4 text-sm text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-indigo-500 focus:bg-white focus:ring-2 focus:ring-indigo-100"
            />
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-6 flex items-center gap-1 overflow-x-auto rounded-xl border border-slate-200 bg-white p-1">
          <TabButton
            active={activeTab === "my-folders"}
            onClick={() => setActiveTab("my-folders")}
          >
            My Folders
          </TabButton>

          <TabButton
            active={activeTab === "shared"}
            onClick={() => setActiveTab("shared")}
          >
            Shared With Me
          </TabButton>

          <TabButton
            active={activeTab === "recent"}
            onClick={() => setActiveTab("recent")}
          >
            Recent
          </TabButton>
        </div>

        {/* Stats */}
        <div className="mb-7 grid grid-cols-1 gap-4 sm:grid-cols-3">
          <StatCard
            icon={FaFolder({})}
            label="My Folders"
            value={myFolders.length}
          />

          <StatCard
            icon={FaShareAlt({})}
            label="Shared With Me"
            value={sharedFolders.length}
          />

          <StatCard
            icon={FaUsers({})}
            label="Accessible Documents"
            value={folders.reduce(
              (total, folder) => total + folder.files.length,
              0,
            )}
          />
        </div>

        {/* Folder heading */}
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">
              {activeTab === "my-folders"
                ? "My Folders"
                : activeTab === "shared"
                  ? "Shared With Me"
                  : "Recent Documents"}
            </h2>

            <p className="mt-1 text-sm text-slate-500">
              {displayedFolders.length} folder
              {displayedFolders.length !== 1 ? "s" : ""}
            </p>
          </div>
        </div>

        {/* Folders */}
        {displayedFolders.length > 0 ? (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {displayedFolders.map((folder) => (
              <button
                key={folder.id}
                type="button"
                onClick={() => setActiveFolder(folder)}
                className="group text-left"
              >
                <div className="h-full rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:border-indigo-200 hover:shadow-md">
                  <div className="mb-5 flex items-start justify-between">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
                      {FaFolder({
                        className: "text-xl",
                      })}
                    </div>

                    <div className="flex items-center gap-2">
                      <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-medium text-slate-500">
                        {getVisibilityLabel(folder.visibility)}
                      </span>

                      {FaEllipsisH({
                        className:
                          "text-slate-300 transition group-hover:text-slate-500",
                      })}
                    </div>
                  </div>

                  <h3 className="truncate font-semibold text-slate-900">
                    {folder.name}
                  </h3>

                  <p className="mt-1 line-clamp-2 min-h-[40px] text-sm leading-5 text-slate-500">
                    {folder.description || "No description added."}
                  </p>

                  <div className="mt-5 flex items-center justify-between border-t border-slate-100 pt-4">
                    <span className="text-xs text-slate-400">
                      {folder.files.length}{" "}
                      {folder.files.length === 1 ? "file" : "files"}
                    </span>

                    <div className="flex -space-x-2">
                      {folder.members.slice(0, 3).map((member) => {
                        const person = getStaff(member.staffId);

                        return (
                          <div
                            key={member.staffId}
                            title={person?.name}
                            className="flex h-7 w-7 items-center justify-center rounded-full border-2 border-white bg-indigo-100 text-[10px] font-semibold text-indigo-700"
                          >
                            {person?.name
                              .split(" ")
                              .map((name) => name[0])
                              .join("")
                              .slice(0, 2)}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="mt-3 flex items-center justify-between text-[11px] text-slate-400">
                    <span>Updated {folder.updatedAt}</span>

                    {folder.allowFileUploads && (
                      <span className="flex items-center gap-1">
                        {FaCloudUploadAlt({})}
                        Uploads allowed
                      </span>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        ) : (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-16 text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
              {FaFolder({ className: "text-xl" })}
            </div>

            <h3 className="mt-4 font-semibold text-slate-800">
              No folders found
            </h3>

            <p className="mx-auto mt-1 max-w-md text-sm text-slate-500">
              Create a folder to start organizing and sharing your workplace
              documents.
            </p>

            <button
              type="button"
              onClick={() => setShowCreateFolder(true)}
              className="mt-5 inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700"
            >
              {FaPlus({ className: "text-xs" })}
              Create Folder
            </button>
          </div>
        )}
      </div>

      {/* Create folder modal */}
      {showCreateFolder && (
        <Modal
          title="Create Folder"
          description="Create a workspace for your documents and decide who can access it."
          onClose={() => setShowCreateFolder(false)}
        >
          <div className="space-y-5">
            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700">
                Folder Name
              </label>

              <input
                type="text"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                placeholder="e.g. Project Documentation"
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700">
                Description
              </label>

              <textarea
                value={newFolderDescription}
                onChange={(e) => setNewFolderDescription(e.target.value)}
                placeholder="What will this folder contain?"
                rows={3}
                className="w-full resize-none rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
              />
            </div>

            <div>
              <label className="mb-3 block text-sm font-medium text-slate-700">
                Who can access this folder?
              </label>

              <div className="space-y-2">
                <VisibilityOption
                  value="private"
                  selected={newFolderVisibility === "private"}
                  onClick={() => setNewFolderVisibility("private")}
                  title="Only Me"
                  description="Only you can access this folder."
                  icon={FaLock({})}
                />

                <VisibilityOption
                  value="selected"
                  selected={newFolderVisibility === "selected"}
                  onClick={() => setNewFolderVisibility("selected")}
                  title="Selected Staff"
                  description="Only staff members you choose can access it."
                  icon={FaUsers({})}
                />

                <VisibilityOption
                  value="everyone"
                  selected={newFolderVisibility === "everyone"}
                  onClick={() => setNewFolderVisibility("everyone")}
                  title="Everyone"
                  description="All staff members can access this folder."
                  icon={FaShareAlt({})}
                />
              </div>
            </div>

            <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 p-4">
              <div>
                <p className="text-sm font-semibold text-slate-800">
                  Allow members to add files
                </p>

                <p className="mt-1 text-xs text-slate-500">
                  Members can upload documents to this folder.
                </p>
              </div>

              <Toggle
                checked={allowFileUploads}
                onChange={() => setAllowFileUploads(!allowFileUploads)}
              />
            </div>

            <div className="flex justify-end gap-3 border-t border-slate-100 pt-5">
              <button
                type="button"
                onClick={() => setShowCreateFolder(false)}
                className="rounded-xl border border-slate-200 px-5 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-50"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={createFolder}
                disabled={!newFolderName.trim()}
                className="rounded-xl bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Create Folder
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Folder Workspace                                                           */
/* -------------------------------------------------------------------------- */

function FolderWorkspace({
  folder,
  staff,
  currentUserId,
  getFileIcon,
  getStaff,
  getVisibilityLabel,
  onBack,
  onManageAccess,
  showManageAccess,
  setShowManageAccess,
}: {
  folder: Folder;
  staff: Staff[];
  currentUserId: string;
  getFileIcon: (type: DocumentFile["type"]) => React.ReactNode;
  getStaff: (id: string) => Staff | undefined;
  getVisibilityLabel: (visibility: Visibility) => string;
  onBack: () => void;
  onManageAccess: () => void;
  showManageAccess: boolean;
  setShowManageAccess: (value: boolean) => void;
}) {
  const [search, setSearch] = useState("");

  const filteredFiles = folder.files.filter((file) =>
    file.name.toLowerCase().includes(search.toLowerCase()),
  );

  const isOwner = folder.ownerId === currentUserId;

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-6">
      <div className="mx-auto w-full max-w-[1600px]">
        {/* Back */}
        <button
          type="button"
          onClick={onBack}
          className="mb-5 flex items-center gap-2 text-sm font-medium text-slate-500 transition hover:text-indigo-600"
        >
          {FaArrowLeft({})}
          Back to Document Center
        </button>

        {/* Folder header */}
        <div className="mb-6 rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="p-5 md:p-6">
            <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
              <div className="flex items-start gap-4">
                <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-600">
                  {FaFolderOpen({ className: "text-2xl" })}
                </div>

                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <h1 className="text-xl font-bold text-slate-900">
                      {folder.name}
                    </h1>

                    <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-medium text-slate-500">
                      {getVisibilityLabel(folder.visibility)}
                    </span>
                  </div>

                  <p className="mt-1 max-w-2xl text-sm text-slate-500">
                    {folder.description || "No description added."}
                  </p>

                  <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-slate-400">
                    <span>
                      {folder.files.length}{" "}
                      {folder.files.length === 1 ? "file" : "files"}
                    </span>

                    <span>
                      {folder.members.length}{" "}
                      {folder.members.length === 1 ? "member" : "members"}
                    </span>

                    {folder.allowFileUploads && (
                      <span className="flex items-center gap-1">
                        {FaCloudUploadAlt({})}
                        Members can upload
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={onManageAccess}
                  className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-50"
                >
                  {FaUsers({})}
                  Manage Access
                </button>

                <button
                  type="button"
                  className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700"
                >
                  {FaCloudUploadAlt({})}
                  Add Files
                </button>
              </div>
            </div>
          </div>

          {/* Members */}
          <div className="border-t border-slate-100 px-5 py-4 md:px-6">
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-xs font-medium text-slate-400">
                MEMBERS
              </span>

              <div className="flex flex-wrap gap-2">
                {folder.members.map((member) => {
                  const person = getStaff(member.staffId);

                  return (
                    <div
                      key={member.staffId}
                      className="flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 py-1.5 pl-1.5 pr-3"
                    >
                      <div className="flex h-6 w-6 items-center justify-center rounded-full bg-indigo-100 text-[9px] font-bold text-indigo-700">
                        {person?.name
                          .split(" ")
                          .map((name) => name[0])
                          .join("")
                          .slice(0, 2)}
                      </div>

                      <span className="text-xs font-medium text-slate-600">
                        {person?.name}
                      </span>

                      <span className="text-[10px] text-slate-400">
                        {member.role}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* File toolbar */}
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="font-semibold text-slate-900">Files</h2>
            <p className="mt-1 text-xs text-slate-500">
              Documents stored in this workspace.
            </p>
          </div>

          <div className="relative w-full sm:w-72">
            <div className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
              {FaSearch({ className: "text-xs" })}
            </div>

            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search files..."
              className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-9 pr-3 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
            />
          </div>
        </div>

        {/* Files */}
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          {filteredFiles.length > 0 ? (
            <div className="divide-y divide-slate-100">
              {filteredFiles.map((file) => (
                <div
                  key={file.id}
                  className="flex flex-col gap-4 px-5 py-4 transition hover:bg-slate-50 md:flex-row md:items-center md:justify-between"
                >
                  <div className="flex min-w-0 items-center gap-4">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-slate-50 text-xl">
                      {getFileIcon(file.type)}
                    </div>

                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-slate-800">
                        {file.name}
                      </p>

                      <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-400">
                        <span>{file.size}</span>
                        <span>•</span>
                        <span>Uploaded by {file.uploadedBy}</span>
                        <span>•</span>
                        <span>{file.updatedAt}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between gap-5 md:justify-end">
                    <span className="rounded-full bg-slate-100 px-3 py-1.5 text-[11px] font-medium text-slate-500">
                      {getVisibilityLabel(file.access)}
                    </span>

                    <button
                      type="button"
                      className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                    >
                      {FaEllipsisH({})}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="px-6 py-20 text-center">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
                {FaFolder({ className: "text-xl" })}
              </div>

              <h3 className="mt-4 font-semibold text-slate-800">
                No files yet
              </h3>

              <p className="mt-1 text-sm text-slate-500">
                Upload documents to start building this workspace.
              </p>

              <button
                type="button"
                className="mt-5 inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700"
              >
                {FaCloudUploadAlt({ className: "text-sm" })}
                Add Files
              </button>
            </div>
          )}
        </div>
      </div>

      {showManageAccess && (
        <ManageAccessModal
          folder={folder}
          staff={staff}
          isOwner={isOwner}
          onClose={() => setShowManageAccess(false)}
        />
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Manage Access                                                              */
/* -------------------------------------------------------------------------- */

function ManageAccessModal({
  folder,
  staff,
  isOwner,
  onClose,
}: {
  folder: Folder;
  staff: Staff[];
  isOwner: boolean;
  onClose: () => void;
}) {
  const [members, setMembers] = useState(folder.members);

  const updateRole = (staffId: string, role: Role) => {
    setMembers((prev) =>
      prev.map((member) =>
        member.staffId === staffId
          ? {
              ...member,
              role,
            }
          : member,
      ),
    );
  };

  const addMember = (staffId: string) => {
    if (members.some((member) => member.staffId === staffId)) return;

    setMembers((prev) => [
      ...prev,
      {
        staffId,
        role: "viewer",
      },
    ]);
  };

  const removeMember = (staffId: string) => {
    setMembers((prev) =>
      prev.filter((member) => member.staffId !== staffId),
    );
  };

  return (
    <Modal
      title="Manage Access"
      description={`Control who can access "${folder.name}".`}
      onClose={onClose}
      wide
    >
      <div className="space-y-5">
        <div className="rounded-xl border border-indigo-100 bg-indigo-50 p-4">
          <div className="flex gap-3">
            <div className="mt-0.5 text-indigo-600">
              {FaShareAlt({})}
            </div>

            <div>
              <p className="text-sm font-semibold text-indigo-900">
                Folder access
              </p>

              <p className="mt-1 text-xs leading-5 text-indigo-700">
                Members with Viewer access can read files. Editors can
                collaborate and modify documents according to the folder
                permissions.
              </p>
            </div>
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-slate-200">
          {members.map((member) => {
            const person = staff.find(
              (item) => item.id === member.staffId,
            );

            return (
              <div
                key={member.staffId}
                className="flex items-center justify-between gap-4 border-b border-slate-100 px-4 py-3 last:border-b-0"
              >
                <div className="flex min-w-0 items-center gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-xs font-bold text-indigo-700">
                    {person?.name
                      .split(" ")
                      .map((name) => name[0])
                      .join("")
                      .slice(0, 2)}
                  </div>

                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-slate-800">
                      {person?.name}
                    </p>

                    <p className="truncate text-xs text-slate-400">
                      {person?.role}
                    </p>
                  </div>
                </div>

                {member.role === "owner" ? (
                  <span className="rounded-full bg-indigo-50 px-3 py-1.5 text-[11px] font-semibold text-indigo-600">
                    Owner
                  </span>
                ) : (
                  <div className="flex items-center gap-2">
                    <select
                      value={member.role}
                      onChange={(e) =>
                        updateRole(
                          member.staffId,
                          e.target.value as Role,
                        )
                      }
                      className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs outline-none focus:border-indigo-500"
                    >
                      <option value="viewer">Viewer</option>
                      <option value="editor">Editor</option>
                    </select>

                    {isOwner && (
                      <button
                        type="button"
                        onClick={() => removeMember(member.staffId)}
                        className="rounded-lg px-2 py-2 text-xs text-red-500 hover:bg-red-50"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {isOwner && (
          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700">
              Add Staff
            </label>

            <select
              defaultValue=""
              onChange={(e) => {
                if (e.target.value) {
                  addMember(e.target.value);
                  e.target.value = "";
                }
              }}
              className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
            >
              <option value="">Select staff member...</option>

              {staff
                .filter(
                  (person) =>
                    !members.some(
                      (member) => member.staffId === person.id,
                    ),
                )
                .map((person) => (
                  <option key={person.id} value={person.id}>
                    {person.name} — {person.role}
                  </option>
                ))}
            </select>
          </div>
        )}

        <div className="flex justify-end border-t border-slate-100 pt-5">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700"
          >
            Done
          </button>
        </div>
      </div>
    </Modal>
  );
}

/* -------------------------------------------------------------------------- */
/* Components                                                                 */
/* -------------------------------------------------------------------------- */

function TabButton({
  children,
  active,
  onClick,
}: {
  children: React.ReactNode;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`whitespace-nowrap rounded-lg px-4 py-2.5 text-sm font-medium transition ${
        active
          ? "bg-indigo-50 text-indigo-700"
          : "text-slate-500 hover:bg-slate-50 hover:text-slate-700"
      }`}
    >
      {children}
    </button>
  );
}

function StatCard({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
}) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-center justify-between">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
          {icon}
        </div>

        <span className="text-2xl font-bold text-slate-900">
          {value}
        </span>
      </div>

      <p className="mt-4 text-sm font-medium text-slate-500">
        {label}
      </p>
    </div>
  );
}

function VisibilityOption({
  value,
  selected,
  onClick,
  title,
  description,
  icon,
}: {
  value: Visibility;
  selected: boolean;
  onClick: () => void;
  title: string;
  description: string;
  icon: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-center gap-3 rounded-xl border p-4 text-left transition ${
        selected
          ? "border-indigo-300 bg-indigo-50/50"
          : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"
      }`}
    >
      <div
        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${
          selected
            ? "bg-indigo-100 text-indigo-600"
            : "bg-slate-100 text-slate-500"
        }`}
      >
        {icon}
      </div>

      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-slate-800">
          {title}
        </p>

        <p className="mt-0.5 text-xs text-slate-500">
          {description}
        </p>
      </div>

      <div
        className={`flex h-5 w-5 items-center justify-center rounded-full border-2 ${
          selected
            ? "border-indigo-600"
            : "border-slate-300"
        }`}
      >
        {selected && (
          <div className="h-2.5 w-2.5 rounded-full bg-indigo-600" />
        )}
      </div>
    </button>
  );
}

function Toggle({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onChange}
      className={`relative h-6 w-11 shrink-0 rounded-full transition ${
        checked ? "bg-indigo-600" : "bg-slate-300"
      }`}
    >
      <span
        className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition ${
          checked ? "left-[22px]" : "left-0.5"
        }`}
      />
    </button>
  );
}

function Modal({
  title,
  description,
  children,
  onClose,
  wide = false,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
  onClose: () => void;
  wide?: boolean;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm">
      <div
        className={`max-h-[90vh] w-full overflow-y-auto rounded-2xl bg-white shadow-2xl ${
          wide ? "max-w-2xl" : "max-w-lg"
        }`}
      >
        <div className="flex items-start justify-between border-b border-slate-100 px-5 py-4">
          <div>
            <h2 className="text-lg font-bold text-slate-900">
              {title}
            </h2>

            {description && (
              <p className="mt-1 text-sm text-slate-500">
                {description}
              </p>
            )}
          </div>

          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          >
            {FaTimes({})}
          </button>
        </div>

        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}